import {
  ROUTINE_TRIGGER_GMAIL,
  ROUTINE_TRIGGER_OUTLOOK,
  type GmailTriggerDraft,
  type OutlookTriggerDraft,
  type Routine,
  type RoutineFormDraft,
  type RoutineTriggerDraft,
} from '@littlebird/sdk';

import { startChatWithPrompt } from '@/features/chat/start-chat-with-prompt';

import {
  outlookDirectionLabel,
  setOutlookFilters,
} from './outlook-trigger-filters';

import type { GmailAccountOption } from './components/routine-gmail-trigger-row';

export type EmailTriggerDraft = GmailTriggerDraft | OutlookTriggerDraft;

/** Marks the row handed to chat, so the trigger the agent saves for it replaces it when the form reopens. */
export const markSentToChat = (
  triggers: RoutineTriggerDraft[],
  index: number
): RoutineTriggerDraft[] =>
  triggers.map((trigger, i) =>
    i === index &&
    (trigger.kind === ROUTINE_TRIGGER_GMAIL ||
      trigger.kind === ROUTINE_TRIGGER_OUTLOOK)
      ? { ...trigger, sentToChat: true }
      : trigger
  );

const gmailCurrentState = (
  trigger: GmailTriggerDraft,
  gmailAccounts: GmailAccountOption[]
) => {
  const mailbox =
    gmailAccounts.find((account) => account.id === trigger.connectedAccountId)
      ?.account_label ??
    trigger.connectedAccountId ??
    'any connected Gmail account';
  const direction = trigger.labelIds.includes('SENT')
    ? trigger.labelIds.includes('INBOX')
      ? 'received or sent'
      : 'sent'
    : 'received';
  return `Current mailbox: ${JSON.stringify(mailbox)}; mail: ${direction}; filter: ${JSON.stringify(trigger.query)}`;
};

const outlookCurrentState = (
  trigger: OutlookTriggerDraft,
  outlookAccounts: GmailAccountOption[]
) => {
  const mailbox =
    outlookAccounts.find((account) => account.id === trigger.connectedAccountId)
      ?.account_label ??
    trigger.connectedAccountId ??
    'no Outlook account chosen yet';
  // An Outlook edit resets each filter it leaves out, so "no other filters" tells the agent
  // that a filter cleared in the form stays cleared
  const set = setOutlookFilters(trigger);
  const filters = set.length
    ? `${set
        .map(
          ({ label, values }) =>
            `${label}: ${values.map((value) => JSON.stringify(value)).join(', ')}`
        )
        .join('; ')}; no other filters`
    : 'none';
  return `Current Outlook account: ${JSON.stringify(mailbox)}; mail: ${outlookDirectionLabel(trigger.folders)}; filters: ${filters}`;
};

export const startEmailTriggerChat = ({
  routine,
  draft,
  trigger,
  gmailAccounts,
  outlookAccounts,
}: {
  routine?: Routine;
  draft: RoutineFormDraft;
  trigger: EmailTriggerDraft;
  gmailAccounts: GmailAccountOption[];
  outlookAccounts: GmailAccountOption[];
}) => {
  const isOutlook = trigger.kind === ROUTINE_TRIGGER_OUTLOOK;
  const savedIndex =
    routine?.triggers
      .filter((entry) => entry.kind === trigger.kind)
      .findIndex((entry) => entry.id === trigger.id) ?? -1;
  const provider = isOutlook ? 'Outlook email' : 'email';
  const target = routine
    ? `::routine{id=${routine.referenceId}}`
    : draft.title.trim() || 'a new routine';
  const request = !routine
    ? `Help me configure ${isOutlook ? 'an Outlook email' : 'an email'} filter for ${target}`
    : savedIndex < 0
      ? `Help me add ${isOutlook ? 'an Outlook email' : 'an email'} trigger for ${target}`
      : `Help me configure ${provider} trigger ${savedIndex + 1} for ${target}`;

  startChatWithPrompt(
    [
      request,
      trigger.kind === ROUTINE_TRIGGER_GMAIL
        ? gmailCurrentState(trigger, gmailAccounts)
        : outlookCurrentState(trigger, outlookAccounts),
      'Keep the other triggers unchanged',
    ].join('\n')
  );
};
